import { supabase } from '../lib/supabase';

export const documentLodgementService = {
  // ============================================================================
  // ABLE PAYROLL DOCUMENTS
  // ============================================================================

  async getAllAbleDocuments() {
    const { data, error } = await supabase
      ?.from('able_payroll_documents')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      documentType: row?.document_type,
      documentPeriod: row?.document_period,
      documentStatus: row?.document_status,
      payrollRunId: row?.payroll_run_id,
      filingPeriodStart: row?.filing_period_start,
      filingPeriodEnd: row?.filing_period_end,
      totalEmployees: row?.total_employees,
      totalGrossWages: row?.total_gross_wages,
      totalDeductions: row?.total_deductions,
      totalNetWages: row?.total_net_wages,
      documentUrl: row?.document_url,
      templateVersion: row?.template_version,
      generatedAt: row?.generated_at,
      validatedAt: row?.validated_at,
      submittedAt: row?.submitted_at,
      confirmedAt: row?.confirmed_at,
      ableReferenceNumber: row?.able_reference_number,
      validationErrors: row?.validation_errors,
      submissionResponse: row?.submission_response,
      generatedBy: row?.generated_by,
      submittedBy: row?.submitted_by,
      metadata: row?.metadata,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getAbleDocumentById(id) {
    const { data, error } = await supabase
      ?.from('able_payroll_documents')
      ?.select('*')
      ?.eq('id', id)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      documentType: data?.document_type,
      documentPeriod: data?.document_period,
      documentStatus: data?.document_status,
      payrollRunId: data?.payroll_run_id,
      filingPeriodStart: data?.filing_period_start,
      filingPeriodEnd: data?.filing_period_end,
      totalEmployees: data?.total_employees,
      totalGrossWages: data?.total_gross_wages,
      totalDeductions: data?.total_deductions,
      totalNetWages: data?.total_net_wages,
      documentUrl: data?.document_url,
      generatedAt: data?.generated_at,
      submittedAt: data?.submitted_at,
      confirmedAt: data?.confirmed_at,
      ableReferenceNumber: data?.able_reference_number,
      validationErrors: data?.validation_errors,
      submissionResponse: data?.submission_response
    };
  },

  async createAbleDocument(document) {
    const { data, error } = await supabase
      ?.from('able_payroll_documents')
      ?.insert({
        document_type: document?.documentType,
        document_period: document?.documentPeriod,
        document_status: document?.documentStatus || 'draft',
        payroll_run_id: document?.payrollRunId,
        filing_period_start: document?.filingPeriodStart,
        filing_period_end: document?.filingPeriodEnd,
        total_employees: document?.totalEmployees || 0,
        total_gross_wages: document?.totalGrossWages || 0,
        total_deductions: document?.totalDeductions || 0,
        total_net_wages: document?.totalNetWages || 0,
        template_version: document?.templateVersion,
        generated_by: document?.generatedBy,
        metadata: document?.metadata
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      documentType: data?.document_type,
      documentPeriod: data?.document_period,
      documentStatus: data?.document_status
    };
  },

  async updateAbleDocumentStatus(id, status, updates = {}) {
    const updateData = {
      document_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'generated') {
      updateData.generated_at = new Date()?.toISOString();
      if (updates?.documentUrl) {
        updateData.document_url = updates?.documentUrl;
      }
    } else if (status === 'validated') {
      updateData.validated_at = new Date()?.toISOString();
    } else if (status === 'submitted') {
      updateData.submitted_at = new Date()?.toISOString();
      if (updates?.submittedBy) {
        updateData.submitted_by = updates?.submittedBy;
      }
    } else if (status === 'confirmed') {
      updateData.confirmed_at = new Date()?.toISOString();
      if (updates?.ableReferenceNumber) {
        updateData.able_reference_number = updates?.ableReferenceNumber;
      }
    } else if (status === 'rejected' || status === 'failed') {
      if (updates?.validationErrors) {
        updateData.validation_errors = updates?.validationErrors;
      }
    }

    if (updates?.submissionResponse) {
      updateData.submission_response = updates?.submissionResponse;
    }

    const { data, error } = await supabase
      ?.from('able_payroll_documents')
      ?.update(updateData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      documentStatus: data?.document_status,
      submittedAt: data?.submitted_at,
      confirmedAt: data?.confirmed_at
    };
  },

  // ============================================================================
  // IRC TAX DOCUMENTS (SWT & SOE)
  // ============================================================================

  async getAllIrcDocuments() {
    const { data, error } = await supabase
      ?.from('irc_tax_documents')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      documentType: row?.document_type,
      documentPeriod: row?.document_period,
      documentStatus: row?.document_status,
      payrollRunId: row?.payroll_run_id,
      filingPeriodStart: row?.filing_period_start,
      filingPeriodEnd: row?.filing_period_end,
      totalEmployees: row?.total_employees,
      totalTaxableIncome: row?.total_taxable_income,
      totalSwtWithheld: row?.total_swt_withheld,
      totalEmployeeSuper: row?.total_employee_super,
      totalEmployerSuper: row?.total_employer_super,
      documentUrl: row?.document_url,
      templateVersion: row?.template_version,
      generatedAt: row?.generated_at,
      validatedAt: row?.validated_at,
      submittedAt: row?.submitted_at,
      confirmedAt: row?.confirmed_at,
      ircReferenceNumber: row?.irc_reference_number,
      validationErrors: row?.validation_errors,
      submissionResponse: row?.submission_response,
      generatedBy: row?.generated_by,
      submittedBy: row?.submitted_by,
      metadata: row?.metadata,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getIrcDocumentById(id) {
    const { data, error } = await supabase
      ?.from('irc_tax_documents')
      ?.select('*')
      ?.eq('id', id)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      documentType: data?.document_type,
      documentPeriod: data?.document_period,
      documentStatus: data?.document_status,
      payrollRunId: data?.payroll_run_id,
      filingPeriodStart: data?.filing_period_start,
      filingPeriodEnd: data?.filing_period_end,
      totalEmployees: data?.total_employees,
      totalTaxableIncome: data?.total_taxable_income,
      totalSwtWithheld: data?.total_swt_withheld,
      totalEmployeeSuper: data?.total_employee_super,
      totalEmployerSuper: data?.total_employer_super,
      documentUrl: data?.document_url,
      generatedAt: data?.generated_at,
      submittedAt: data?.submitted_at,
      confirmedAt: data?.confirmed_at,
      ircReferenceNumber: data?.irc_reference_number,
      validationErrors: data?.validation_errors,
      submissionResponse: data?.submission_response
    };
  },

  async getIrcDocumentsByType(documentType) {
    const { data, error } = await supabase
      ?.from('irc_tax_documents')
      ?.select('*')
      ?.eq('document_type', documentType)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      documentPeriod: row?.document_period,
      documentStatus: row?.document_status,
      totalSwtWithheld: row?.total_swt_withheld,
      submittedAt: row?.submitted_at,
      ircReferenceNumber: row?.irc_reference_number
    })) || [];
  },

  async createIrcDocument(document) {
    const { data, error } = await supabase
      ?.from('irc_tax_documents')
      ?.insert({
        document_type: document?.documentType,
        document_period: document?.documentPeriod,
        document_status: document?.documentStatus || 'draft',
        payroll_run_id: document?.payrollRunId,
        filing_period_start: document?.filingPeriodStart,
        filing_period_end: document?.filingPeriodEnd,
        total_employees: document?.totalEmployees || 0,
        total_taxable_income: document?.totalTaxableIncome || 0,
        total_swt_withheld: document?.totalSwtWithheld || 0,
        total_employee_super: document?.totalEmployeeSuper || 0,
        total_employer_super: document?.totalEmployerSuper || 0,
        template_version: document?.templateVersion,
        generated_by: document?.generatedBy,
        metadata: document?.metadata
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      documentType: data?.document_type,
      documentPeriod: data?.document_period,
      documentStatus: data?.document_status
    };
  },

  async updateIrcDocumentStatus(id, status, updates = {}) {
    const updateData = {
      document_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'generated') {
      updateData.generated_at = new Date()?.toISOString();
      if (updates?.documentUrl) {
        updateData.document_url = updates?.documentUrl;
      }
    } else if (status === 'validated') {
      updateData.validated_at = new Date()?.toISOString();
    } else if (status === 'submitted') {
      updateData.submitted_at = new Date()?.toISOString();
      if (updates?.submittedBy) {
        updateData.submitted_by = updates?.submittedBy;
      }
    } else if (status === 'confirmed') {
      updateData.confirmed_at = new Date()?.toISOString();
      if (updates?.ircReferenceNumber) {
        updateData.irc_reference_number = updates?.ircReferenceNumber;
      }
    } else if (status === 'rejected' || status === 'failed') {
      if (updates?.validationErrors) {
        updateData.validation_errors = updates?.validationErrors;
      }
    }

    if (updates?.submissionResponse) {
      updateData.submission_response = updates?.submissionResponse;
    }

    const { data, error } = await supabase
      ?.from('irc_tax_documents')
      ?.update(updateData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      documentStatus: data?.document_status,
      submittedAt: data?.submitted_at,
      confirmedAt: data?.confirmed_at
    };
  },

  // ============================================================================
  // DOCUMENT LODGEMENT HISTORY
  // ============================================================================

  async getLodgementHistory() {
    const { data, error } = await supabase
      ?.from('document_lodgement_history')
      ?.select(`
        *,
        government_filing_lodgments (
          filing_type,
          portal_type,
          lodgment_status
        ),
        able_payroll_documents (
          document_type,
          document_period
        ),
        irc_tax_documents (
          document_type,
          document_period
        )
      `)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      lodgmentId: row?.lodgment_id,
      ableDocumentId: row?.able_document_id,
      ircDocumentId: row?.irc_document_id,
      lodgementType: row?.lodgement_type,
      lodgementPeriod: row?.lodgement_period,
      lodgementStatus: row?.lodgement_status,
      portalType: row?.portal_type,
      submittedAt: row?.submitted_at,
      confirmedAt: row?.confirmed_at,
      portalReference: row?.portal_reference,
      confirmationDocumentUrl: row?.confirmation_document_url,
      notes: row?.notes,
      metadata: row?.metadata,
      lodgment: row?.government_filing_lodgments ? {
        filingType: row?.government_filing_lodgments?.filing_type,
        portalType: row?.government_filing_lodgments?.portal_type,
        lodgmentStatus: row?.government_filing_lodgments?.lodgment_status
      } : null,
      ableDocument: row?.able_payroll_documents ? {
        documentType: row?.able_payroll_documents?.document_type,
        documentPeriod: row?.able_payroll_documents?.document_period
      } : null,
      ircDocument: row?.irc_tax_documents ? {
        documentType: row?.irc_tax_documents?.document_type,
        documentPeriod: row?.irc_tax_documents?.document_period
      } : null,
      createdAt: row?.created_at
    })) || [];
  },

  async createLodgementHistory(history) {
    const { data, error } = await supabase
      ?.from('document_lodgement_history')
      ?.insert({
        lodgment_id: history?.lodgmentId,
        able_document_id: history?.ableDocumentId,
        irc_document_id: history?.ircDocumentId,
        lodgement_type: history?.lodgementType,
        lodgement_period: history?.lodgementPeriod,
        lodgement_status: history?.lodgementStatus,
        portal_type: history?.portalType,
        submitted_at: history?.submittedAt,
        confirmed_at: history?.confirmedAt,
        portal_reference: history?.portalReference,
        confirmation_document_url: history?.confirmationDocumentUrl,
        notes: history?.notes,
        metadata: history?.metadata
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      lodgementType: data?.lodgement_type,
      lodgementStatus: data?.lodgement_status
    };
  },

  // ============================================================================
  // DOCUMENT GENERATION HELPERS
  // ============================================================================

  async generateSwtDocument(payrollRunId, filingPeriod) {
    try {
      // Get payroll run details
      const { data: payrollRun, error: runError } = await supabase
        ?.from('payroll_runs')
        ?.select('*')
        ?.eq('id', payrollRunId)
        ?.single();
      
      if (runError) throw runError;

      // Get payroll details for employee count
      const { data: payrollDetails, error: detailsError } = await supabase
        ?.from('payroll_details')
        ?.select('*')
        ?.eq('payroll_run_id', payrollRunId);
      
      if (detailsError) throw detailsError;

      // Create IRC Tax document
      const document = await this.createIrcDocument({
        documentType: 'swt_monthly',
        documentPeriod: filingPeriod,
        documentStatus: 'generated',
        payrollRunId: payrollRunId,
        filingPeriodStart: payrollRun?.pay_period_start,
        filingPeriodEnd: payrollRun?.pay_period_end,
        totalEmployees: payrollDetails?.length || 0,
        totalTaxableIncome: payrollRun?.total_gross || 0,
        totalSwtWithheld: payrollRun?.total_swt || 0,
        totalEmployeeSuper: 0,
        totalEmployerSuper: 0,
        templateVersion: 'v3.0',
        generatedBy: 'System'
      });

      // Update status to generated
      await this.updateIrcDocumentStatus(document?.id, 'generated', {
        documentUrl: `/documents/swt/${document?.id}.pdf`
      });

      return document;
    } catch (error) {
      throw new Error(`Failed to generate SWT document: ${error?.message}`);
    }
  },

  async generateSoeDocument(payrollRunId, filingPeriod) {
    try {
      // Get payroll run details
      const { data: payrollRun, error: runError } = await supabase
        ?.from('payroll_runs')
        ?.select('*')
        ?.eq('id', payrollRunId)
        ?.single();
      
      if (runError) throw runError;

      // Get payroll details for calculations
      const { data: payrollDetails, error: detailsError } = await supabase
        ?.from('payroll_details')
        ?.select('*')
        ?.eq('payroll_run_id', payrollRunId);
      
      if (detailsError) throw detailsError;

      // Calculate totals
      let totalEmployeeSuper = 0;
      let totalEmployerSuper = 0;

      payrollDetails?.forEach(detail => {
        totalEmployeeSuper += parseFloat(detail?.employee_super || 0);
        totalEmployerSuper += parseFloat(detail?.employer_super || 0);
      });

      // Create IRC Tax document
      const document = await this.createIrcDocument({
        documentType: 'soe_monthly',
        documentPeriod: filingPeriod,
        documentStatus: 'generated',
        payrollRunId: payrollRunId,
        filingPeriodStart: payrollRun?.pay_period_start,
        filingPeriodEnd: payrollRun?.pay_period_end,
        totalEmployees: payrollDetails?.length || 0,
        totalTaxableIncome: payrollRun?.total_gross || 0,
        totalSwtWithheld: payrollRun?.total_swt || 0,
        totalEmployeeSuper: totalEmployeeSuper,
        totalEmployerSuper: totalEmployerSuper,
        templateVersion: 'v3.0',
        generatedBy: 'System'
      });

      // Update status to generated
      await this.updateIrcDocumentStatus(document?.id, 'generated', {
        documentUrl: `/documents/soe/${document?.id}.pdf`
      });

      return document;
    } catch (error) {
      throw new Error(`Failed to generate SOE document: ${error?.message}`);
    }
  }
};

export default documentLodgementService;